/**
 * Keeps the signed-in session in step with what the server says about its token.
 *
 * · Renewal: a token past half its life comes back renewed in the
 *   `X-Renewed-Token` response header (backend/app/auth.py). It replaces the
 *   stored token, so a pad in daily use never reaches the expiry mid-service.
 * · Sign-out: a 401 on a call that carried the CURRENT token means that token
 *   is dead. The session ends and the app lands on the login screen, rather
 *   than every KOT and board read failing behind a green "Connected" badge
 *   (C04, 2026-10-29: 8 minutes of 401s on a dead token).
 *
 * Both act only when the request's bearer is the session's current token, so a
 * late answer to an earlier user's (or an already renewed) token changes nothing.
 * useSession listens for the two events and updates its state.
 */
const SESSION_KEY = "vibez-session";
export const RENEWED_TOKEN_HEADER = "X-Renewed-Token";
export const TOKEN_RENEWED_EVENT = "vibez:token-renewed";
export const SESSION_EXPIRED_EVENT = "vibez:session-expired";

// 401 by design: a wrong PIN on the login screen or in a manager-authorize dialog.
const CREDENTIAL_ENDPOINTS = ["/auth/login", "/auth/authorize"];

let installed = false;

const readSession = () => {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
  } catch {
    return null;
  }
};

export const bearerOf = value => {
  const match = /^Bearer\s+(.+)$/i.exec(String(value || ""));
  return match ? match[1] : null;
};

export function noteAuthResponse({ url, requestToken, status, renewedToken }) {
  if (!requestToken) return;
  const session = readSession();
  if (!session?.token || session.token !== requestToken) return;

  if (status === 401) {
    if (CREDENTIAL_ENDPOINTS.some(path => String(url || "").includes(path))) return;
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    return;
  }
  if (renewedToken && renewedToken !== requestToken) {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, token: renewedToken }));
    } catch {
      /* storage unavailable: the state update below still carries it */
    }
    window.dispatchEvent(new CustomEvent(TOKEN_RENEWED_EVENT, { detail: { token: renewedToken } }));
  }
}

function fetchAuthorization(input, init) {
  const headers = init?.headers ?? (typeof Request !== "undefined" && input instanceof Request ? input.headers : null);
  if (!headers) return null;
  if (typeof headers.get === "function") return headers.get("Authorization");
  if (Array.isArray(headers)) return headers.find(([k]) => String(k).toLowerCase() === "authorization")?.[1] ?? null;
  const key = Object.keys(headers).find(k => k.toLowerCase() === "authorization");
  return key ? headers[key] : null;
}

/** Wrap window.fetch once: the mesh sync, log upload and many screens use fetch, not axios. */
export function installFetchSessionGuard() {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await original(input, init);
    try {
      noteAuthResponse({
        url: typeof input === "string" ? input : input?.url,
        requestToken: bearerOf(fetchAuthorization(input, init)),
        status: response.status,
        renewedToken: response.headers?.get?.(RENEWED_TOKEN_HEADER) || null,
      });
    } catch {
      /* never let session bookkeeping break the caller's request */
    }
    return response;
  };
}
